// @ts-nocheck

import type { ComponentType } from 'react';
import { createSelector } from 'reselect';
import styled, { css } from 'styled-components';

import * as BurgerIcons from '../../assets/svg/burger-menu';
import * as TabBarIcons from '../../assets/svg/tab-bar';
import config from '../../config/app.config';
import { RedirectServices, getRedirectUrl } from '../../config/urls';
import { getLang, __ } from '../../i18n/l10n';
import { isGridsAllowed } from '../../modules/grids/utils';
import { selectIsKLEnabled } from '../../modules/koh-lanta/selectors/runtime';
import {
  getHistoryFromProps,
  getLocationPathnameFromProps,
  getOpenExternalLinkFromProps,
  getToggleMenuFromProps,
} from '../../selectors/_propsGetters_';
import { selectRuntimeIsWTVEnabled } from '../../selectors/runtime/runtimePrimary.selector';
import { Locales } from '../../types/common';
import { type Selector } from '../../types/selectors';
import Routes from '../../utils/routes';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';

const sharedIconsStyle = css`
  color: #c1c9cf;
  height: 2.7em;
  width: 2.7em;
`;

const HomeIcon = styled(TabBarIcons.Home)`
  ${sharedIconsStyle}
`;

const JdeIcon = styled(BurgerIcons.Jde)`
  ${sharedIconsStyle}
`;

const GridsIcon = styled(BurgerIcons.Grids)`
  ${sharedIconsStyle}
`;

const ShopIcon = styled(BurgerIcons.Shop)`
  ${sharedIconsStyle}
`;

const StatsIcon = styled(BurgerIcons.Stats)`
  ${sharedIconsStyle}
`;

const NewsIcon = styled(BurgerIcons.News)`
  ${sharedIconsStyle}
`;

const WinamaxTVIcon = styled(BurgerIcons.WinaTV)`
  ${sharedIconsStyle}
`;

const ExtrasIcon = styled(BurgerIcons.Extras)`
  ${sharedIconsStyle}
`;

type NavigationBurgerMenuPropsType = {
  items: Array<{
    label: string;
    onClick: (...params: unknown) => unknown;
    counter: number | null;
    icon: ComponentType<unknown>;
    isLive?: boolean;
  }>;
};

const selectNavigationBurgerMenuProps: Selector<NavigationBurgerMenuPropsType> = createSelector(
  [
    selectIsKLEnabled,
    selectRuntimeIsWTVEnabled,
    getToggleMenuFromProps,
    getHistoryFromProps,
    getLocationPathnameFromProps,
    getOpenExternalLinkFromProps,
  ],
  (isKLEnabled, winaTvIsLive, toggleMenu, history, pathname, openExternalLink) => {
    const lang = getLang();

    const jdeUrl = getRedirectUrl(RedirectServices.FANTASY);
    const shopUrl = getRedirectUrl(RedirectServices.SHOP);
    const statsUrl = getRedirectUrl(RedirectServices.STATS);
    const newsUrl = getRedirectUrl(RedirectServices.NEWS);
    const wtvFullscreenUrl = `${config.webHost[lang]}/winamax-tv`;

    const isWinaTvEnabled = [Locales.FR, Locales.EN].indexOf(lang) !== -1;

    const openLink = (url) => {
      toggleMenu();
      openExternalLink(url, { auth: true });
    };

    const items = [];

    const gridsAllowed = isGridsAllowed(lang);

    if (isKLEnabled) {
      items.push({
        label: __('Extras'),
        onClick: () => {
          history.push(RoutesRedirectionFactory.getExtrasRouteConfig());
          toggleMenu();
        },
        icon: ExtrasIcon,
      });
    }

    if (Routes.isGrids(pathname, { exact: false })) {
      items.push({
        label: __('Sports bets'),
        onClick: () => {
          history.push(RoutesRedirectionFactory.getHomeRouteConfig());
          toggleMenu();
        },
        icon: HomeIcon,
      });
    } else if (gridsAllowed) {
      items.push({
        label: __('Grids'),
        onClick: () => {
          history.push(RoutesRedirectionFactory.getGridsRouteConfig());
          toggleMenu();
        },
        icon: GridsIcon,
      });
    }

    if (gridsAllowed) {
      items.push({
        label: 'JDE',
        onClick: () => openLink(jdeUrl),
        icon: JdeIcon,
      });
    }

    items.push(
      {
        label: __('Stats'),
        onClick: () => openLink(statsUrl),
        icon: StatsIcon,
      },
      {
        label: __('Shop'),
        onClick: () => openLink(shopUrl),
        icon: ShopIcon,
      },
      {
        label: __('News'),
        onClick: () => openLink(newsUrl),
        icon: NewsIcon,
      },
    );

    if (isWinaTvEnabled) {
      items.push({
        label: 'Winamax TV',
        onClick: () => openLink(wtvFullscreenUrl),
        icon: WinamaxTVIcon,
        isLive: winaTvIsLive,
      });
    }

    return {
      items,
    };
  },
);

export default selectNavigationBurgerMenuProps;
